import FJMessageStore from '../../store/FJMessageStore';
import FJLocalStore from '../../store/FJLocalStore';
import {
    setPromptSlateNodes,
    setSelectedPreset,
    resetState,
    setPromptStatus,
    setUploadStatus,
    setCurModel,
    setInputImages,
    removeInputImage,
    clearInputImages,
    applyFirstInputImage,
    setSliceRatio,
    setSliceResolution,
    setPromptMaxLength,
    setSlicePrivacySettingAction,
    setSliceCreationListAction,
    setSliceTaskStatusAction,
    setSliceFreeNumCreationAction,
} from '../slices/FJSliceAIPhotoEditor';
import {
    SMART_IMAGE_INSTRUCTION_TEMPLATES,
    AI_PHOTO_EDITOR_MODELS,
    AI_PHOTO_EDITOR_MODELS_VALUE,
    OUT_MODEL_MAP,
} from '../../config/dataConfig/FJAIPhotoEditorData';
import FJUtil from '../../util/FJUtil';
import FJConstant from '../../constants/FJConstant';
import {
    MAX_COMPRESS_IMAGE_SIZE,
    UPLOAD_IMAGE_FILE_TYPE_NO_NEED_TRANSCODE,
} from '../../config/dataConfig/FJGenerateData';
import FJCompressedImageStore from '../../store/FJCompressedImageStore';
import FJRequirementsUtil from '../../util/FJRequirementsUtil';
import FJActionCommon from './FJActionCommon';
import FJActionModal from './FJActionModal';
import FJConfig from '../../config/FJConfig';
import FJImageStore from '../../store/FJImageStore';
import FJS3Config from '../../config/FJS3Config';
import FJNetworkStore from '../../store/FJNetworkStore';
import FJLoadLocalforageStore from '../../store/FJLoadLocalforageStore';
import FJAIVideoGeneratorConfig from '../../config/appConfig/FJAIVideoGeneratorConfig';
import FJErrorMessageUtil from '../../util/FJErrorMessageUtil';
import FJAppConstant from '../../constants/FJAppConstant.js';
import FJAIPhotoEditorConfig from '../../config/appConfig/FJAIPhotoEditorConfig.js';
import FJUserSelectionStore, { SELECTION_KEYS } from '../../store/FJUserSelectionStore';
import FJActionCommunity from './FJActionCommunity.js';
import { AI_COMMUNITY_TYPES } from '../../config/dataConfig/FJCommunityData.js';

let _curEventId = null;
let _aiOptimizingStatus = false;
const _getAIPhotoEditorMaxImages = curModel => {
    return AI_PHOTO_EDITOR_MODELS.find(item => item.value === curModel)?.supportImageReferenceNum || 5;
};
const _getPromptResult = value => {
    const nodes = value.map(item => {
        if (item.type === 'preInput' && item.children[0].text.length === 0) {
            return {
                ...item,
                children: [{ ...item.children[0], text: item.preValue }],
            };
        }
        return item;
    });

    return FJRequirementsUtil.getTextBySlateNodes(nodes);
};

const _localData = data => {
    let textData = {};
    Object.keys(data).forEach(key => {
        if (Array.isArray(data[key])) {
            textData[key] = data[key].map(v => FJLocalStore._(v));
        } else if (key !== 'id') {
            // 不翻译id
            textData[key] = FJLocalStore._(data[key]);
        }
    });
    return textData;
};

const _checkIsCurrentTask = (eventId, curEventId = _curEventId) => {
    return curEventId === eventId;
};

// 根据图片比例计算最接近的输出比例
const _getClosestAspectRatio = (width, height) => {
    const ratio = width / height;

    if (ratio <= 0.67) {
        return '2:3'; // 竖图
    } else if (ratio <= 1) {
        return '1:1'; // 正方形
    } else if (ratio <= 1.5) {
        return '3:2'; // 横图
    } else {
        return '3:2'; // 超宽图，默认使用3:2
    }
};

const FJActionAIPhotoEditor = {
    updateUseFreeLimitAction: function (info) {
        return async dispatch => {
            dispatch(setSliceFreeNumCreationAction(info[FJAIPhotoEditorConfig.freeLimitType]));
        };
    },
    editHistoryItemAction: function (data = {}) {
        return async dispatch => {
            const { prompt, inputImages = [], inputImage, modelType } = data;
            if (prompt) {
                dispatch(setPromptSlateNodes(FJUtil.convertToSlateNodes({ text: prompt })));
            }

            dispatch(this.changeModelAction(modelType));

            let fileList = [];
            dispatch(clearInputImages());
            for (const item of inputImages) {
                const blob = await FJUtil.getBlobByUrl(item);
                const fileName = FJUtil.getFileName(item);
                fileList.push(new File([blob], fileName, { type: 'image/png' }));
            }
            if (inputImage) {
                const blob = await FJUtil.getBlobByUrl(inputImage);
                const fileName = FJUtil.getFileName(inputImage);
                fileList.push(new File([blob], fileName, { type: 'image/png' }));
            }

            dispatch(this.uploadMultipleImagesAction(fileList, true));
        };
    },
    setCreationListAction: function (list) {
        return dispatch => {
            dispatch(setSliceCreationListAction(list));
        };
    },
    setTaskStatusAction: function (value) {
        return dispatch => {
            dispatch(setSliceTaskStatusAction(value));
        };
    },
    setPrivacySettingAction: function (value) {
        return dispatch => {
            dispatch(setSlicePrivacySettingAction(value));
        };
    },
    /**
     * @desc 重置AI图片编辑器
     */
    resetAction: function () {
        return dispatch => {
            dispatch(resetState());
        };
    },

    /**
     * @desc 中断当前生成任务
     */
    cancelTaskAction: function () {
        return dispatch => {
            // 取消优化提示
            dispatch(this.cancelImprovePromptsAction());
            // 取消生成
            dispatch(setSliceTaskStatusAction(FJConstant.DEFAULT));
            dispatch(setSliceCreationListAction([]));
            _curEventId = null;
        };
    },

    editCloseAction: function () {
        return dispatch => {
            dispatch(this.cancelTaskAction());
        };
    },

    // 优化提示
    improvePromptsAction: function () {
        return async (dispatch, getState) => {
            const state = getState().aiPhotoEditor;
            const prompt = _getPromptResult(state.promptSlateNodes);
            dispatch(setPromptStatus(FJConstant.PROGRESS));

            if (!prompt) {
                FJMessageStore.addMessage(FJLocalStore._('please-fill-in-prompt-first'), 'error');
                dispatch(setPromptStatus(FJConstant.DEFAULT));
                return;
            }

            // 检查用户空间，判断登陆状态
            const userSpaceType = await FJUtil.checkUserSpace({
                errorCallback: () => {
                    FJMessageStore.addMessage(FJLocalStore._('ai-optimization-prompts-failed'), 'error');
                },
            });

            if (userSpaceType > 0) {
                dispatch(FJActionCommon.userSpaceChangeAction(userSpaceType));
                dispatch(setPromptStatus(FJConstant.DEFAULT));
                return false;
            }

            try {
                _aiOptimizingStatus = true;
                const res = await FJNetworkStore.optimizeTranslatePrompt({
                    prompt,
                    currentLang: FJLocalStore.currentTag(),
                    type: 'photo_editor',
                });
                if (!_aiOptimizingStatus || !res) {
                    dispatch(setPromptStatus(FJConstant.DEFAULT));
                    return;
                }
                dispatch(setPromptSlateNodes(FJUtil.convertToSlateNodes({ text: res })));
            } catch (error) {
                FJMessageStore.addMessage(FJLocalStore._('failed-to-create-task'), 'error');
            } finally {
                dispatch(setPromptStatus(FJConstant.DEFAULT));
                _aiOptimizingStatus = false;
            }
        };
    },

    // 取消优化提示
    cancelImprovePromptsAction: function () {
        return dispatch => {
            _aiOptimizingStatus = false;
            dispatch(setPromptStatus(FJConstant.DEFAULT));
        };
    },

    // 通过url上传图片
    uploadImageUrlAction: function (resources, options = {}) {
        return async (dispatch, getState) => {
            const originalImages = getState().aiPhotoEditor.inputImages.filter(item => !!item);
            const curModel = getState().aiPhotoEditor.curModel;
            const supportMultiImage =
                AI_PHOTO_EDITOR_MODELS.find(item => item.value === curModel)?.supportImageReferenceNum > 1;
            let imageInfos = [];
            if (resources.length > 1) {
                imageInfos = await Promise.all(
                    resources.map(async resource => {
                        const { size, resource: url, name, id } = resource;
                        if (size && (size.width > MAX_COMPRESS_IMAGE_SIZE || size.height > MAX_COMPRESS_IMAGE_SIZE)) {
                            const imageInfo = await FJCompressedImageStore.compressImageByUrl(url, {
                                name: name,
                            }).catch(() => {
                                throw new Error('compress image failed');
                            });
                            return imageInfo;
                        } else {
                            return {
                                name: name,
                                src: url,
                                width: size.width,
                                height: size.height,
                                id: id,
                            };
                        }
                    }),
                );
            } else if (resources.length === 1) {
                const { size, name, id } = options;
                if (size && (size.width > MAX_COMPRESS_IMAGE_SIZE || size.height > MAX_COMPRESS_IMAGE_SIZE)) {
                    const imageInfo = await FJCompressedImageStore.compressImageByUrl(resources[0], {
                        name: name,
                    }).catch(() => {
                        throw new Error('compress image failed');
                    });
                    imageInfos.push(imageInfo);
                } else {
                    imageInfos = [
                        {
                            name: name,
                            src: resources[0],
                            width: size.width,
                            height: size.height,
                            id: id,
                        },
                    ];
                }
            }

            // 统一使用inputImages
            if (supportMultiImage) {
                if (originalImages.length + imageInfos.length > _getAIPhotoEditorMaxImages(curModel)) {
                    FJMessageStore.addMessage(
                        FJLocalStore._('maximum-limit-for-uploaded-reference-images-reached'),
                        'error',
                    );
                }
                dispatch(
                    setInputImages(
                        [
                            ...originalImages,
                            ...imageInfos.map(imageInfo => ({ ...imageInfo, type: 'local', source: FJConstant.LOCAL })),
                        ].slice(0, _getAIPhotoEditorMaxImages(curModel)),
                    ),
                );
            } else {
                // 单图，替换第一张
                const newImages = [...originalImages];
                newImages[0] = { ...imageInfos[0], type: 'local', source: FJConstant.LOCAL };
                dispatch(setInputImages(newImages.slice(0, _getAIPhotoEditorMaxImages(curModel))));
            }
        };
    },

    // 单图模式上传
    uploadImageAction: function (file, skipTranscoded = false) {
        return async (dispatch, getState) => {
            const uploadStatus = getState().aiPhotoEditor.uploadStatus;
            const originalImages = getState().aiPhotoEditor.inputImages.filter(item => !!item);
            const curModel = getState().aiPhotoEditor.curModel;
            if (uploadStatus === FJConstant.LOADING) {
                return;
            }

            const __fileError = () => {
                FJMessageStore.addMessage(
                    FJLocalStore._('unsupported-format-please-try-again-with-supported-formats'),
                    'error',
                );
                dispatch(setUploadStatus(FJConstant.DEFAULT));
            };

            const isImage = FJUtil.isImage(file, false, UPLOAD_IMAGE_FILE_TYPE_NO_NEED_TRANSCODE);

            if (!isImage) {
                if (skipTranscoded) {
                    __fileError();
                    return;
                }
                try {
                    const result = await dispatch(
                        FJActionModal.showCloudTranscodingModalAction({
                            files: [file],
                            mediaType: 'image',
                            outputFormat: 'png',
                            onReupload: newFiles => {
                                if (newFiles && newFiles.length > 0) {
                                    // 重新上传文件
                                    dispatch(this.uploadImageAction(newFiles[0]));
                                }
                            },
                        }),
                    );
                    // 直接使用转码后的文件
                    file = result.files[0];
                } catch (e) {
                    if (e.cause === FJConstant.INVALID) {
                        __fileError();
                    }
                    dispatch(setUploadStatus(FJConstant.DEFAULT));
                    return;
                }
            }

            const imageInfo = await FJCompressedImageStore.compressImageByFile(file);
            if (imageInfo) {
                FJUtil.eaLog('ai_photo_editor_upload', {
                    count: 1,
                    formats: file.type,
                });
                dispatch(setUploadStatus(FJConstant.SUCCESS));
                const newImages = [...originalImages];
                newImages[0] = { ...imageInfo, type: 'local', source: FJConstant.LOCAL };
                dispatch(setInputImages(newImages.slice(0, _getAIPhotoEditorMaxImages(curModel))));
            } else {
                __fileError();
            }
        };
    },

    // 多图模式上传
    uploadMultipleImagesAction: function (files, skipTranscoded = false) {
        return async (dispatch, getState) => {
            const uploadStatus = getState().aiPhotoEditor.uploadStatus;
            const originalImages = getState().aiPhotoEditor.inputImages.filter(item => !!item);
            const curModel = getState().aiPhotoEditor.curModel;

            if (files.length + originalImages.length > _getAIPhotoEditorMaxImages(curModel)) {
                FJMessageStore.addMessage(
                    FJLocalStore._('maximum-limit-for-uploaded-reference-images-reached'),
                    'error',
                );
            }

            if (uploadStatus === FJConstant.LOADING) {
                return;
            }

            const __fileError = () => {
                FJMessageStore.addMessage(
                    FJLocalStore._('unsupported-format-please-try-again-with-supported-formats'),
                    'error',
                );
                dispatch(setUploadStatus(FJConstant.DEFAULT));
            };

            try {
                const filesArray = Array.from(files).slice(
                    0,
                    _getAIPhotoEditorMaxImages(curModel) - originalImages.length,
                );
                if (filesArray.length === 0) {
                    dispatch(setUploadStatus(FJConstant.DEFAULT));
                    return;
                }

                const validFiles = filesArray.filter(file =>
                    FJUtil.isImage(file, false, UPLOAD_IMAGE_FILE_TYPE_NO_NEED_TRANSCODE),
                );
                const transcodedFiles = filesArray.filter(
                    file => !FJUtil.isImage(file, false, UPLOAD_IMAGE_FILE_TYPE_NO_NEED_TRANSCODE),
                );

                const _uploadImages = imageInfos => {
                    if (imageInfos.length > 0) {
                        FJUtil.eaLog('ai_photo_editor_upload', {
                            count: imageInfos.length,
                            formats: validFiles.map(f => f.type).join(','),
                        });
                        dispatch(setUploadStatus(FJConstant.SUCCESS));
                        let newImages = [...originalImages, ...imageInfos];
                        dispatch(setInputImages(newImages.slice(0, _getAIPhotoEditorMaxImages(curModel))));
                    } else {
                        __fileError();
                    }
                };

                // 统一处理文件的函数
                const processFiles = async filesToProcess => {
                    dispatch(setUploadStatus(FJConstant.LOADING));
                    const imagePromises = filesToProcess.map(async file => {
                        const imageInfo = await FJCompressedImageStore.compressImageByFile(file);
                        return imageInfo ? { ...imageInfo, type: 'local', source: FJConstant.LOCAL } : null;
                    });
                    const imageResults = await Promise.all(imagePromises);
                    return imageResults.filter(img => img !== null);
                };

                // 收集所有要处理的文件
                let filesToProcess = [...validFiles];

                if (transcodedFiles.length > 0) {
                    if (skipTranscoded) {
                        FJMessageStore.addMessage(
                            FJLocalStore._('unsupported-format-please-try-again-with-supported-formats'),
                            'error',
                        );
                        // 只处理合法文件
                        const validImages = await processFiles(filesToProcess);
                        _uploadImages(validImages);
                        return;
                    }

                    // 使用转码功能
                    try {
                        const result = await dispatch(
                            FJActionModal.showCloudTranscodingModalAction({
                                files: transcodedFiles,
                                mediaType: 'image',
                                outputFormat: 'png',
                                inputOptions: {
                                    multiple: true,
                                },
                                onReupload: newFiles => {
                                    if (newFiles && newFiles.length > 0) {
                                        // 重新上传文件
                                        dispatch(this.uploadMultipleImagesAction(newFiles));
                                    }
                                },
                            }),
                        );

                        // 转码成功后，添加转码文件到处理列表
                        filesToProcess = [...filesToProcess, ...result.files];
                    } catch (e) {
                        if (e.cause === FJConstant.INVALID) {
                            __fileError();
                        }
                        dispatch(setUploadStatus(FJConstant.DEFAULT));
                    }
                }

                // 统一处理所有文件
                if (filesToProcess.length > 0) {
                    const allValidImages = await processFiles(filesToProcess);
                    _uploadImages(allValidImages);
                }
            } catch (error) {
                __fileError();
            }
        };
    },

    // 从多图列表中移除图片
    removeImageFromInputImagesAction: function (index) {
        return dispatch => {
            dispatch(removeInputImage(index));
        };
    },

    // 清空多图列表
    clearInputImagesAction: function () {
        return dispatch => {
            dispatch(clearInputImages());
        };
    },

    // 设置编辑指令
    setPromptSlateNodesAction: function (instructions) {
        return dispatch => {
            dispatch(setPromptSlateNodes(instructions));
        };
    },

    // 选择预设效果
    selectPresetAction: function (preset) {
        return (dispatch, getState) => {
            dispatch(setSelectedPreset(preset));

            // 当选择 ai-group-portrait 预设时，如果当前模型不支持多图，自动切换到第一个支持多图的模型
            if (preset.id === 'ai-group-portrait') {
                const state = getState();
                const currentModel = AI_PHOTO_EDITOR_MODELS.find(model => model.value === state.aiPhotoEditor.curModel);

                if (!(currentModel?.supportImageReferenceNum > 1)) {
                    const firstMultiImageModel = AI_PHOTO_EDITOR_MODELS.find(
                        model => model.supportImageReferenceNum > 1,
                    );
                    if (firstMultiImageModel) {
                        dispatch(this.changeModelAction(firstMultiImageModel.value));
                    }
                }
            }

            const template = SMART_IMAGE_INSTRUCTION_TEMPLATES.find(item => item.id === preset.id);

            FJUtil.eaLog('pre_effect_click', {
                category: preset.id,
            });

            dispatch(setPromptSlateNodes(FJUtil.convertToSlateNodes(_localData(template))));
        };
    },

    // 重置AI图片编辑器
    resetAIPhotoEditorAction: function () {
        return dispatch => {
            dispatch(resetState());
        };
    },

    // 修改模型
    changeModelAction: function (modelValue, isInit = false) {
        return async (dispatch, getState) => {
            const model = OUT_MODEL_MAP[modelValue] || modelValue;
            dispatch(setCurModel(model));
            FJUserSelectionStore.save(FJAppConstant.AI_PHOTO_EDITOR, SELECTION_KEYS.CUR_MODEL, model);
            const curModelData = AI_PHOTO_EDITOR_MODELS.find(item => item.value === model);

            // 更新提示词字符限制
            const promptLimit = curModelData?.promptCharacterLimit || 5000;
            dispatch(setPromptMaxLength(promptLimit));

            // 如果新模型支持当前比例则继承，否则默认选择第一个
            const curRatio = getState().aiPhotoEditor.curRatio;
            if (curModelData?.supportRatios?.includes(curRatio) && !isInit) {
                dispatch(setSliceRatio(curRatio));
            } else {
                dispatch(setSliceRatio(curModelData?.supportRatios?.[0] || ''));
            }

            // 如果新模型支持当前分辨率则继承，否则默认选择第一个
            const curResolution = getState().aiPhotoEditor.curResolution;
            if (curModelData?.supportResolutions?.find(item => item.value === curResolution) && !isInit) {
                dispatch(setSliceResolution(curResolution));
            } else {
                dispatch(setSliceResolution(curModelData?.supportResolutions?.[0]?.value || null));
            }
            FJUtil.eaLog('ai_photo_editor_model_select', { mode: curModelData?.name });
        };
    },

    // 生成失败
    _generateErrorAction: function () {
        return () => {
            FJMessageStore.addMessage(FJLocalStore._('failed-to-create-task'), 'error');
        };
    },

    // 生成失败，重试
    _errorAndReLoadAction: function (errorCode, modelValue) {
        return (dispatch, getState) => {
            const creationList = getState().aiPhotoEditor.creationList;
            const currentModelData = AI_PHOTO_EDITOR_MODELS.find(item => item.value === modelValue);

            const reLoadList = creationList.map(i => ({
                ...i,
                isLoading: false,
                isReload: true,
                errorCode: errorCode,
                model: modelValue,
            }));
            dispatch(setSliceCreationListAction(reLoadList));
            dispatch(setSliceTaskStatusAction(FJConstant.ERROR));
            FJUtil.eaLog('ai_photo_editor_generate_failed', { mode: currentModelData?.name });
        };
    },

    // 生成逻辑
    onGenerateAction: function (data) {
        return async (dispatch, getState) => {
            const currentTaskId = new Date().getTime();
            _curEventId = currentTaskId;
            const tool = FJUtil.getToolName();

            const state = getState().aiPhotoEditor;
            const {
                promptStatus,
                promptSlateNodes,
                promptMaxLength,
                curModel,
                inputImages,
                curRatio,
                curResolution,
                privacyStatus,
            } = state;
            const promptResult = _getPromptResult(promptSlateNodes);

            let originalPrompt = promptResult;
            let originalModel = curModel;
            let inputImagesList = inputImages.filter(item => !!item);

            // 如果data存在，则使用data中的数据
            if (data) {
                originalPrompt = data.originalPrompt;
                originalModel = data.model;
                inputImagesList = data.inputImages || [];
            }
            const currentModel = AI_PHOTO_EDITOR_MODELS.find(item => item.value === originalModel);
            inputImagesList = inputImagesList.slice(0, currentModel?.supportImageReferenceNum || 5);
            FJUtil.eaLog('ai_photo_editor_generate_click', { mode: currentModel?.name });
            const useCredit = currentModel?.useCredit || 0;
            const { creditNumber, freeNumCreation } = getState().common;
            const canFreeCreate = freeNumCreation > 0;
            const isFree = creditNumber < useCredit && canFreeCreate;

            dispatch(FJActionCommon.setHeaderTabIndexAction(FJConstant.CREATIONS));
            dispatch(setSliceTaskStatusAction(FJConstant.START));

            // 1. 前端用户输入检查
            const isMultiImageMode = currentModel?.supportImageReferenceNum > 1;
            if (!inputImagesList || inputImagesList.length === 0) {
                FJMessageStore.addMessage(FJLocalStore._('please-upload-an-image-first'), 'error');
                dispatch(setSliceTaskStatusAction(FJConstant.DEFAULT));
                return;
            }

            const _prompt = originalPrompt.replace(/\s/g, '');
            if (_prompt.length === 0 || originalPrompt.length === 0 || promptStatus === FJConstant.PROGRESS) {
                FJMessageStore.addMessage(FJLocalStore._('please-fill-in-prompt-first'), 'error');
                dispatch(setSliceTaskStatusAction(FJConstant.DEFAULT));
                return;
            }

            // 如果prompt长度超过最大长度，则截取前promptMaxLength个字符
            if (originalPrompt.length > promptMaxLength) {
                originalPrompt = originalPrompt.slice(0, promptMaxLength);
            }

            if (!originalModel) {
                FJMessageStore.addMessage(FJLocalStore._('please-select-a-model'), 'error');
                dispatch(setSliceTaskStatusAction(FJConstant.DEFAULT));
                return;
            }
            // 2. user space 检查
            const userSpaceType = await FJUtil.checkUserSpace({
                errorCallback: () => {
                    FJMessageStore.addMessage(FJLocalStore._('failed-to-create-task'), 'error');
                    dispatch(setSliceTaskStatusAction(FJConstant.DEFAULT));
                },
            });

            if (userSpaceType > 0) {
                dispatch(FJActionCommon.userSpaceChangeAction(userSpaceType));
                dispatch(setSliceTaskStatusAction(FJConstant.DEFAULT));
                return;
            }

            // 用于编辑页ai工具在执行任务过程中的阻断，不需要做任何处理
            if (!_checkIsCurrentTask(currentTaskId)) {
                return;
            }

            // 3. 额度检查
            if (creditNumber < useCredit && !canFreeCreate) {
                dispatch(FJActionModal.showUpgradeModalAction());
                dispatch(setSliceTaskStatusAction(FJConstant.DEFAULT));
                return;
            }

            // 统一使用inputImages的第一张图片作为主图片
            const mainImage = inputImagesList[0];
            const placeHolderList = FJUtil.createPlaceHolderList({
                count: 1,
                width: mainImage.width,
                height: mainImage.height,
                type: FJConstant.CREATIONS,
                uploadImageInfo: mainImage,
                generateInfo: {
                    originalPrompt,
                    inputImages: inputImagesList,
                    model: originalModel,
                    ratio: curRatio,
                    resolution: curResolution,
                },
            });
            dispatch(setSliceCreationListAction(placeHolderList));

            // 4. 图片上传s3
            let imageUrl = mainImage?.src;
            let inputImageUrls = [];
            let waterfallRatio = 1;

            try {
                if (isMultiImageMode) {
                    // 多图模式：上传所有图片到S3
                    const uploadPromises = inputImagesList.map(async image => {
                        const file = await FJUtil.getBlobByUrl(image.src);
                        const id = await FJUtil.hashEncode(image.src);
                        const key = FJConfig.originalImageKey + `${id}.png`;
                        const result = await FJImageStore.upLoadFileToS3Async(file, key);
                        if (result) {
                            return FJS3Config.S3_SERVER_HOST + key;
                        }
                        return null;
                    });

                    const uploadResults = await Promise.all(uploadPromises);
                    inputImageUrls = uploadResults.filter(url => url !== null);

                    if (inputImageUrls.length === 0) {
                        dispatch(this._generateErrorAction());
                        return;
                    }

                    // 使用第一张图片作为主图片
                    imageUrl = inputImageUrls[0];
                } else {
                    // 单图模式：只上传第一张图片到S3
                    const firstImage = inputImagesList[0];
                    const file = await FJUtil.getBlobByUrl(firstImage.src);
                    const id = await FJUtil.hashEncode(firstImage.src);
                    const key = FJConfig.originalImageKey + `${id}.png`;
                    const result = await FJImageStore.upLoadFileToS3Async(file, key);
                    if (result) {
                        imageUrl = FJS3Config.S3_SERVER_HOST + key;
                        inputImageUrls = [imageUrl]; // 单图模式也设置inputImageUrls
                    } else {
                        dispatch(this._generateErrorAction());
                        return;
                    }
                }

                // 用于编辑页ai工具在执行任务过程中的阻断，不需要做任何处理
                if (!_checkIsCurrentTask(currentTaskId)) {
                    return;
                }

                try {
                    waterfallRatio = await FJImageStore.getImageRatio(imageUrl);
                } catch (e) {
                    waterfallRatio = await FJImageStore.getImageRatio(imageUrl + `?v=${new Date().getTime()}`);
                }
            } catch (error) {
                dispatch(this._generateErrorAction());
                dispatch(setSliceTaskStatusAction(FJConstant.DEFAULT));
                return;
            }

            // 用于编辑页ai工具在执行任务过程中的阻断，不需要做任何处理
            if (!_checkIsCurrentTask(currentTaskId)) {
                return;
            }

            // 5. 翻译prompt
            let prompt = originalPrompt;
            if (currentModel.needTranslate) {
                try {
                    prompt = await this._translatePrompt(originalPrompt.replace(/\s+/g, ' '));
                } catch (error) {
                    FJMessageStore.addMessage(FJLocalStore._('failed-to-create-task'), 'error');
                    dispatch(setSliceTaskStatusAction(FJConstant.DEFAULT));
                    return;
                }
            }

            // 用于编辑页ai工具在执行任务过程中的阻断，不需要做任何处理
            if (!_checkIsCurrentTask(currentTaskId)) {
                return;
            }

            // 6. 调用接口，创建任务
            const _request = async () => {
                const params = {
                    prompt,
                    originalPrompt,
                    waterfallRatio,
                    model: originalModel,
                    privacyStatus,
                };

                // 如果是多图模式，添加多图参数
                if (isMultiImageMode) {
                    params.inputImages = inputImageUrls;
                    params.isMultiImageMode = isMultiImageMode;
                } else {
                    params.inputImage = imageUrl; // 单图模式 inputImage
                }
                if (currentModel.supportRatios) {
                    params.ratio = curRatio;
                }
                if (currentModel.supportResolutions) {
                    params.resolution = curResolution;
                }

                // 如果是OPENAI_GPT_IMAGE_1模型，添加输出比例参数
                if (originalModel === AI_PHOTO_EDITOR_MODELS_VALUE.OPENAI_GPT_IMAGE_1) {
                    const aspectRatio = _getClosestAspectRatio(mainImage.width, mainImage.height);
                    params.aspectRatio = aspectRatio;
                }

                const res = await FJNetworkStore.aiPhotoEditor(params);
                return res;
            };

            try {
                const eventId = await FJActionCommon.createRequestTask(_request, dispatch);
                if (!eventId) {
                    dispatch(this.generateErrorAction(true));
                    return;
                }
                FJUtil.eaLog('ai generate community privacy settings', {
                    setting: privacyStatus,
                });
                FJUtil.updateAllCreditInfo(isFree);

                //7.轮询任务状态
                const callback = data => {
                    const { type, errorMessage, errorCode } = data;
                    if (type === FJConstant.ERROR) {
                        dispatch(this._errorAndReLoadAction(errorCode, originalModel));
                        const localMessage =
                            FJErrorMessageUtil.getErrorMessageByState(errorMessage) ||
                            FJLocalStore._('generation-failed-please-try-again');
                        localMessage && FJMessageStore.addMessage(localMessage, 'error');
                    }

                    FJUtil.setLocalStorage();
                    if (type === FJConstant.SUCCESS) {
                        FJUtil.eaLog('ai_photo_editor_generate_success', { mode: currentModel?.name });

                        FJUtil.eaLog('ai tool generate success', {
                            tool: FJUtil.getToolName(),
                            'credit&free': isFree ? 'free' : 'credit',
                        });
                    }
                };

                FJUtil.eaLog('ai tool start generating', { 'credit&free': isFree ? 'free' : 'credit', tool });
                dispatch(
                    FJActionCommon.pollingTaskAction([eventId], {
                        callback,
                        isUseFree: isFree,
                        appType: FJAppConstant.AI_PHOTO_EDITOR,
                    }),
                );
            } catch (error) {
                dispatch(this._generateErrorAction());
                dispatch(this._errorAndReLoadAction(501, originalModel));
                dispatch(setSliceTaskStatusAction(FJConstant.DEFAULT));
            }
        };
    },

    // 翻译prompt
    _translatePrompt: async function (prompt) {
        const isEnglish = FJUtil.isEnglish(prompt);

        if (isEnglish) {
            return prompt;
        }
        const postData = [{ text: prompt }];
        const result = await FJNetworkStore.translateValueToLanguage(postData);
        const usePrompt = result[0].translations[0].text;

        return usePrompt;
    },

    // 初始化AI图片编辑器
    initAIPhotoEditorAction: function () {
        return async dispatch => {
            const recId = FJUtil.getQueryString(FJConstant.REC_ID);
            const recIdInfo = recId ? JSON.parse(window.localStorage.getItem(recId)) : {};

            const effectId = FJUtil.getQueryString('effect_id');
            const query_model_type = FJUtil.getQueryString('model');

            const { fromType } = recIdInfo;
            if (fromType === FJConstant.COMMUNITY) {
                dispatch(FJActionCommon.onRecreateAction(recIdInfo));
                return;
            }

            if (effectId) {
                dispatch(this.selectPresetAction({ id: effectId }));
            }

            if (query_model_type && Object.values(AI_PHOTO_EDITOR_MODELS_VALUE).includes(query_model_type)) {
                dispatch(this.changeModelAction(query_model_type));
            } else {
                const cachedModel = FJUserSelectionStore.load(
                    FJAppConstant.AI_PHOTO_EDITOR,
                    SELECTION_KEYS.CUR_MODEL,
                    AI_PHOTO_EDITOR_MODELS[2]?.value || '',
                    AI_PHOTO_EDITOR_MODELS,
                );
                if (cachedModel) {
                    dispatch(this.changeModelAction(cachedModel, true));
                }
            }

            if (recIdInfo) {
                window.localStorage.removeItem(recId);
                dispatch(this.handleRecInfoAction(recIdInfo, { query_model_type }));
            }
        };
    },
    handleRecInfoAction: function (recIdInfo, options = {}) {
        return async dispatch => {
            const { query_model_type } = options;
            const { prompt, imageUrl, fileList, avator, model: _model, input_image, input_images } = recIdInfo;

            const model = query_model_type || _model;
            if (fileList && fileList.length > 0) {
                const fileInfo = fileList[0]?.info;
                if (!fileInfo) return;
                const data = await FJLoadLocalforageStore.getItem(fileList[0].key);
                const file = new File([data.data], fileInfo.name, { type: fileInfo.type });
                dispatch(this.uploadImageAction(file, true));
            } else if (avator) {
                const blob = await FJUtil.getBlobByUrl(avator);
                const fileName = FJUtil.getFileName(avator);
                const file = new File([blob], fileName, { type: 'image/png' });
                dispatch(this.uploadImageAction(file, true));
            } else if (imageUrl) {
                const blob = await FJUtil.getBlobByUrl(imageUrl);
                // TODO: image name
                const file = new File([blob], 'Image.png', { type: 'image/png' });
                dispatch(this.uploadImageAction(file, true));
            } else if (input_image) {
                const blob = await FJUtil.getBlobByUrl(input_image);
                const fileName = FJUtil.getFileName(input_image);
                const file = new File([blob], fileName, { type: 'image/png' });
                dispatch(this.uploadImageAction(file, true));
            } else if (input_images) {
                let fileList = [];
                for (const item of input_images) {
                    const blob = await FJUtil.getBlobByUrl(item);
                    const fileName = FJUtil.getFileName(item);
                    fileList.push(new File([blob], fileName, { type: 'image/png' }));
                }
                dispatch(this.uploadMultipleImagesAction(fileList, true));
            }

            if (prompt) {
                dispatch(setPromptSlateNodes(FJUtil.convertToSlateNodes({ text: prompt })));
            }
            if (model) {
                dispatch(this.changeModelAction(model));
            }
        };
    },

    // 重试
    retryAction: function (id) {
        return async (dispatch, getState) => {
            const creationList = getState().aiPhotoEditor.creationList;
            const item = creationList.find(item => item.id === id);

            if (item) {
                dispatch(setUploadStatus(FJConstant.SUCCESS));
                dispatch(setPromptStatus(FJConstant.DEFAULT));
                dispatch(setSliceTaskStatusAction(FJConstant.DEFAULT));
                dispatch(setSliceRatio(item.generateInfo.ratio));
                dispatch(setSliceResolution(item.generateInfo.resolution));
                dispatch(this.changeModelAction(item.generateInfo.model));

                dispatch(this.onGenerateAction(item.generateInfo));
            }
        };
    },

    // 下载创作区图片
    downloadCreationAction: function (callback) {
        return async (dispatch, getState) => {
            const { creationList } = getState().aiPhotoEditor;
            if (creationList.length === 0) return;
            const ids = creationList.map(item => item.id);
            dispatch(FJActionCommon.multiDownloadAction(ids, 'creation', callback));
        };
    },

    // 跳转图片生视频
    animateImageAction: function (isModel = false) {
        return async (dispatch, getState) => {
            const tool = FJUtil.getToolName();
            FJUtil.eaLog('ai_image_tool_to_video_generator_click', {
                tool: isModel ? tool + ' window' : tool + ' banner',
            });

            const creationList = getState().aiPhotoEditor.creationList;
            const avator = creationList[0]?.imgUrl || '';
            if (!avator) return;
            const recId = `${Date.now()}${Math.random().toString(36).substr(2, 9)}`;
            localStorage.setItem(recId, JSON.stringify({ avator }));
            const toolName = FJUtil.getAIToolHref(FJAIVideoGeneratorConfig.currentMenuName);
            const url = FJUtil.getCurrentUrl() + toolName.slice(1) + '?rec_id=' + recId;

            window.open(url, '_blank');
        };
    },

    // 创作区继续编辑
    continueEditAction: function () {
        return async (dispatch, getState) => {
            const { creationList } = getState().aiPhotoEditor;
            if (creationList.length === 0) return;
            const creationImageUrl = creationList[0]?.imgUrl;
            dispatch(setUploadStatus(FJConstant.LOADING));

            if (creationImageUrl) {
                try {
                    // 下载图片并转换为 File 对象
                    const blob = await FJUtil.getBlobByUrl(creationImageUrl);

                    const fileName = FJUtil.getFileName(creationImageUrl) || 'creation-image.png';
                    const file = new File([blob], fileName, { type: 'image/png' });
                    const imageInfo = await FJCompressedImageStore.compressImageByFile(file);
                    if (imageInfo) {
                        // 替换 inputImages 第一张图片
                        const newImage = { ...imageInfo, type: 'local', source: FJConstant.LOCAL };
                        dispatch(applyFirstInputImage(newImage));
                    } else {
                        FJMessageStore.addMessage(
                            FJLocalStore._('failed-to-process-this-image-please-try-again'),
                            'error',
                        );
                    }
                } catch (error) {
                    FJMessageStore.addMessage(FJLocalStore._('failed-to-process-this-image-please-try-again'), 'error');
                }
            }
            dispatch(setUploadStatus(FJConstant.DEFAULT));
        };
    },
};

export default FJActionAIPhotoEditor;
